import React from 'react';
import cx from 'classnames';
import { ExclusiveAnd } from '../../util/types';
import { AlertIcon } from '../SvgIcons/Alert';
import { CheckmarkCircleIcon } from '../SvgIcons/CheckmarkCircle';
import { InfoFilledIcon } from '../SvgIcons/InfoFilled';
import { RemoveIcon } from '../SvgIcons/Remove';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Link } from '../Link';

import styles from './SectionMessage.module.less';

// need either both href and alt, or neither
type SectionMessageAction = {
  /** Where the link points to */
  href: string;
  /** Text to be displayed for the link */
  alt: string;
};

type SectionMessageCommonProps = {
  /** Determines color and icon, one of `'Confirmation'`, `'Warning'`, `'Error'`, or `'Info'` */
  type: SectionMessageType;
  /** Title for the message */
  title: string;
  /** Main body for the message */
  description?: string;
  /** For showing an arrow after the hyperlink */
  arrow: boolean;
};

export type SectionMessageProps = SectionMessageCommonProps & ExclusiveAnd<SectionMessageAction>;

export type SectionMessageType = 'Confirmation' | 'Warning' | 'Error' | 'Info';

const IconComponent = (type: SectionMessageType) => {
  switch (type) {
    case 'Confirmation':
      return <CheckmarkCircleIcon className={styles.icon} />;
    case 'Error':
      return <RemoveIcon className={styles.icon} />;
    case 'Info':
      return <InfoFilledIcon className={styles.icon} />;
    case 'Warning':
      return <AlertIcon className={styles.icon} />;
    default:
      return <InfoFilledIcon className={styles.icon} />;
  }
};

const ContainerStyles = (type: SectionMessageType) => {
  switch (type) {
    case 'Confirmation':
      return styles.confirmation;
    case 'Error':
      return styles.error;
    case 'Info':
      return styles.info;
    case 'Warning':
      return styles.warning;
    default:
      return styles.info;
  }
};

/**
 * ```import { SectionMessage } from '@zola/zola-ui/src/components/SectionMessage';```
 *
 * Section Message component
 *
 *    - alerts users that something has happened in a section of the screen
 */
const SectionMessage: React.FC<SectionMessageProps> = ({
  type,
  title,
  description,
  href,
  alt,
  arrow,
}) => {
  return (
    <div className={cx(styles.container, styles.icon, ContainerStyles(type))}>
      {IconComponent(type)}
      <div className={styles.content}>
        <h2 className={styles.title}>{title}</h2>
        {description && <p className={styles.description}>{description}</p>}
        {href && (
          <Link size="small" href={href} arrow={arrow}>
            {alt}
          </Link>
        )}
      </div>
    </div>
  );
};

export default SectionMessage;
